import { ref, onUnmounted } from 'vue';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import type SlAlert from '@shoelace-style/shoelace/dist/components/alert/alert.js';

import '@/assets/styles/ui-kit-components/shared.pcss';
import '@/assets/styles/ui-kit-components/toast.pcss';
import { ImageType } from '@/types/common';

type Variant = 'primary' | 'warning' | 'danger' | 'success';
type Options = {
  variant?: Variant;
  duration?: number;
  closable?: boolean;
  countdown?: 'rtl' | 'ltr' | undefined;
};

export type Message = {
  title: string;
  description?: string;
};

const DEFAULT_CONFIG: Options = {
  variant: 'danger',
  duration: 6000,
  countdown: 'rtl',
  closable: true,
};

const containerEl = ref<HTMLElement | null>();

const getElement = (content: string, variant: 'title' | 'description' = 'title') => {
  const elType = variant === 'title' ? 'h4' : 'div';
  const el = document.createElement(elType);
  el.className = variant;
  el.innerText = content;
  return el;
};

const getIcon = (variant: Variant) => {
  const imgPath = getImage(ImageType.Toast, variant);

  if (imgPath) {
    const imgEl = new Image();
    imgEl.src = imgPath;
    imgEl.width = 24;
    imgEl.height = 24;

    imgEl.setAttribute('slot', 'icon');
    return imgEl;
  }

  return null;
};

export const useToast = () => {
  onMounted(() => {
    containerEl.value = document.getElementById('toast-container')!;
  });

  onUnmounted(() => {
    if (containerEl.value) {
      containerEl.value.textContent = '';
    }
  });

  const addToast = (
    message: Message = { title: '' },
    options: Options = { variant: 'primary' }
  ) => {
    try {
      const alertItemEl: SlAlert = Object.assign(
        document.createElement('sl-alert'),
        DEFAULT_CONFIG,
        options
      );

      const titleEl = getElement(message.title);
      alertItemEl.appendChild(titleEl);

      const iconEl = getIcon(options.variant ?? 'primary');
      if (iconEl) {
        alertItemEl.appendChild(iconEl);
      }

      if (message.description) {
        const descriptionEl = getElement(message.description, 'description');
        alertItemEl.appendChild(descriptionEl);
      }

      if (containerEl.value) {
        containerEl.value.appendChild(alertItemEl);
        alertItemEl.toast();

        // Remove the toast after it's hidden.
        alertItemEl.addEventListener('sl-after-hide', () => {
          alertItemEl.remove();
        });
      } else {
        throw new Error('Toast container not found');
      }
    } catch (error) {
      console.error('Error adding toast:', error);
    }
  };

  const addErrorToast = (message: Message, options?: Options) =>
    addToast(message, {
      ...options,
      variant: 'danger',
    });

  const addWarningToast = (message: Message, options?: Options) =>
    addToast(message, {
      ...options,
      variant: 'warning',
    });

  const addSuccessToast = (message: Message, options?: Options) =>
    addToast(message, {
      ...options,
      variant: 'primary',
    });

  const addNotifyToast = (message: Message, options?: Options) =>
    addToast(message, {
      ...options,
      variant: 'success',
    });

  return {
    addToast,
    addErrorToast,
    addWarningToast,
    addSuccessToast,
    addNotifyToast,
  };
};
